import { useEffect, useMemo, useRef, useState } from 'react'
import Chart from 'chart.js/auto'
import { useApp } from '../../../store.jsx'
import { Icon, Avatar } from '../../../components/Common.jsx'
import ProjectsAtRiskCard from '../../../components/projects/ProjectsAtRiskCard.jsx'
import AnnouncementBanner from '../../../components/announcements/AnnouncementBanner.jsx'
import { fmt, greeting } from '../../../data.js'
import { ReportModal } from '../../../components/Modals.jsx'
import {
  getAnalytics,
  sendReminder as apiSendReminder,
  remindAllPending,
  downloadAnalyticsExport,
  listEmployees,
  getReportById,
} from '../../../lib/api'
import { useSession } from '../../../lib/auth-client'

const cssVar = (n) => getComputedStyle(document.documentElement).getPropertyValue(n).trim()

function chartOpts(opts = {}) {
  const tick = cssVar('--tick-color') || '#6B7280'
  const grid = cssVar('--grid-line') || 'rgba(38,38,54,.6)'
  const tipBg = cssVar('--tooltip-bg') || '#14141C'
  const muted = cssVar('--text-muted') || '#94A3B8'
  const text = cssVar('--text') || '#F1F5F9'
  const border = cssVar('--border') || '#262636'
  const isLight = document.documentElement.dataset.theme === 'light'
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: opts.legend
        ? { position: 'top', align: 'end', labels: { color: muted, boxWidth: 10, boxHeight: 10, padding: 14, font: { size: 12 } } }
        : { display: false },
      tooltip: {
        backgroundColor: tipBg,
        borderColor: border,
        borderWidth: 1,
        padding: 12,
        titleColor: isLight ? '#FFFFFF' : text,
        bodyColor: isLight ? '#CBD5E1' : muted,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tick, font: { size: 11 } } },
      y: { grid: { color: grid }, ticks: { color: tick, font: { size: 11 } }, beginAtZero: true },
    },
  }
}

const reasonText = (r) =>
  ({
    phone_not_verified: 'Phone not verified — reminder skipped',
    throttled: 'Already reminded recently — try again later',
    send_failed: 'WhatsApp send failed',
  }[r] || 'Could not send reminder')

const medals = ['🥇', '🥈', '🥉']

export default function OwnerDashboard() {
  const { state, toast, openModal, closeModal, goPage } = useApp()
  const { data: session } = useSession()
  const firstName = (session?.user?.name || state.user.name || 'there').split(' ')[0]
  const [range, setRange] = useState(30)
  const [customFrom, setCustomFrom] = useState("")
  const [customTo, setCustomTo] = useState("")
  const trendRef = useRef(null)
  const heroSparkRef = useRef(null)
  const statSparkRef = useRef(null)
  const donutRef = useRef(null)
  const trendChart = useRef(null)
  const heroChart = useRef(null)
  const statChart = useRef(null)
  const donutChart = useRef(null)

  const [live, setLive] = useState(null)
  const [managerCount, setManagerCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    if (customFrom && customTo) {
      getAnalytics("", customFrom, customTo)
        .then((d) => { if (!cancelled) setLive(d) })
        .catch(() => { if (!cancelled) setLive(null) })
    } else {
      getAnalytics(`${range}d`)
        .then((d) => { if (!cancelled) setLive(d) })
        .catch(() => { if (!cancelled) setLive(null) })
    }
    return () => { cancelled = true }
  }, [range, customFrom, customTo])

  useEffect(() => {
    let cancelled = false
    listEmployees()
      .then(({ employees }) => {
        if (cancelled) return
        const mgrs = (employees || []).filter((u) => u.role === 'Manager').length
        setManagerCount(mgrs)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const teamToday = live?.teamToday || {
    activeTeam: 0, reportsToday: 0, pending: 0, compliance: 0,
    pendingEmployees: [], topPerformers: [], recentFeed: [],
  }
  const activeEmployees = teamToday.activeTeam
  const pending = teamToday.pending
  const compliance = teamToday.compliance
  const todaysReports = teamToday.reportsToday
  const trend = useMemo(() => live?.dailyTrend || [], [live])

  // ── Full trend chart ────────────────────────────────────────
  useEffect(() => {
    if (!trendRef.current) return
    if (trendChart.current) trendChart.current.destroy()
    const labels = trend.map((d) => new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }))
    const submitted = trend.map((d) => d.submitted)
    const expected = trend.map((d) => d.expected)
    trendChart.current = new Chart(trendRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Submitted',
            data: submitted,
            borderColor: '#8B5CF6',
            backgroundColor: (ctx) => {
              const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 240)
              g.addColorStop(0, 'rgba(139,92,246,.4)')
              g.addColorStop(1, 'rgba(139,92,246,0)')
              return g
            },
            fill: true,
            tension: 0.4,
            borderWidth: 2.5,
            pointRadius: 0,
            pointHoverRadius: 5,
          },
          {
            label: 'Expected',
            data: expected,
            borderColor: 'rgba(148,163,184,.4)',
            borderDash: [4, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
          },
        ],
      },
      options: chartOpts({ legend: true }),
    })
    return () => trendChart.current?.destroy()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, state.settings.theme])

  // ── Hero sparkline (last 14 days of submissions) ────────────
  useEffect(() => {
    if (!heroSparkRef.current) return
    if (heroChart.current) heroChart.current.destroy()
    const recent = trend.slice(-14)
    heroChart.current = new Chart(heroSparkRef.current, {
      type: 'line',
      data: {
        labels: recent.map(() => ''),
        datasets: [{
          data: recent.map((d) => d.submitted),
          borderColor: '#7c3aed',
          backgroundColor: 'rgba(124,58,237,.15)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } },
        elements: { line: { borderJoinStyle: 'round' } },
      },
    })
    return () => heroChart.current?.destroy()
  }, [trend])

  // ── Mini stat sparkline (small "reports today" trend) ───────
  useEffect(() => {
    if (!statSparkRef.current) return
    if (statChart.current) statChart.current.destroy()
    const recent = trend.slice(-7)
    statChart.current = new Chart(statSparkRef.current, {
      type: 'bar',
      data: {
        labels: recent.map(() => ''),
        datasets: [{
          data: recent.map((d) => d.submitted),
          backgroundColor: 'rgba(139,92,246,.45)',
          borderRadius: 3,
          barThickness: 6,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false, beginAtZero: true } },
      },
    })
    return () => statChart.current?.destroy()
  }, [trend])

  // ── Donut (today: submitted vs pending) ─────────────────────
  useEffect(() => {
    if (!donutRef.current) return
    if (donutChart.current) donutChart.current.destroy()
    donutChart.current = new Chart(donutRef.current, {
      type: 'doughnut',
      data: {
        labels: ['Submitted', 'Pending'],
        datasets: [{
          data: [todaysReports, pending],
          backgroundColor: ['#10B981', '#EF4444'],
          borderWidth: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { color: cssVar('--text-muted') || '#94A3B8', font: { size: 11 }, boxWidth: 10, padding: 10, usePointStyle: true, pointStyle: 'circle' },
          },
        },
      },
    })
    return () => donutChart.current?.destroy()
  }, [todaysReports, pending, state.settings.theme])

  useEffect(() => {
    let t
    const onResize = () => {
      clearTimeout(t)
      t = setTimeout(() => {
        trendChart.current?.resize()
        heroChart.current?.resize()
        statChart.current?.resize()
        donutChart.current?.resize()
      }, 150)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const top = teamToday.topPerformers
  const pendingEmps = teamToday.pendingEmployees
  const pendingWindow = teamToday.pendingWindow || {}
  const feed = teamToday.recentFeed

  const [reminding, setReminding] = useState({})

  const sendReminder = async (id) => {
    const e = pendingEmps.find((x) => x.id === id)
    setReminding((m) => ({ ...m, [id]: true }))
    try {
      const { sent, failed } = await apiSendReminder(id)
      if (sent?.length) toast(`Reminder sent to ${e?.name || 'employee'} via WhatsApp`, 'success')
      else toast(reasonText(failed?.[0]?.reason), 'error')
    } catch (err) {
      toast(err.message || 'Failed to send reminder', 'error')
    } finally {
      setReminding((m) => ({ ...m, [id]: false }))
    }
  }

  const remindAll = async () => {
    if (!pendingEmps.length) { toast('No pending submissions', 'info'); return }
    try {
      const { sent, failed } = await remindAllPending()
      const msg = `Sent ${sent?.length || 0} reminders` + (failed?.length ? `, ${failed.length} failed` : '')
      toast(msg, sent?.length ? 'success' : 'error')
    } catch (err) {
      toast(err.message || 'Failed to send reminders', 'error')
    }
  }

  const viewReport = async (feedItem) => {
    try {
      const { report: r } = await getReportById(feedItem.id)
      openModal(
        <ReportModal
          report={{
            ...r,
            employee: r.user,
            tasks: (r.reportTasks || []).length
              ? r.reportTasks.map((rt) => ({
                  text: rt.task?.title || '(task)',
                  status: rt.status,
                  note: rt.note,
                  source: rt.task?.source,
                  carried: !!rt.carriedFrom,
                }))
              : [{ text: r.message || '', status: 'done' }],
            blockers: (r.reportTasks || [])
              .filter((rt) => rt.status === 'pending' && rt.note)
              .map((rt) => rt.note),
            aiSummary: r.analysis?.summary || 'AI summary pending...',
            sentiment: r.analysis?.sentiment || 'neutral',
            source: r.source || 'web',
            hoursWorked: 0,
          }}
          onClose={closeModal}
        />,
        'lg',
      )
    } catch (err) {
      toast(err.message || 'Could not load report', 'error')
    }
  }

  const exportData = async () => {
    try {
      toast('Preparing export…', 'info')
      if (customFrom && customTo) {
        await downloadAnalyticsExport("", customFrom, customTo)
      } else {
        await downloadAnalyticsExport(`${range}d`)
      }
      toast('Export downloaded', 'success')
    } catch (err) {
      toast(err.message || 'Export failed', 'error')
    }
  }

  const openDateMenu = () =>
    openModal(
      <>
        <div className="modal-head">
          <h3>Date range</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[7, 14, 30, 60, 90].map((d) => (
            <button key={d} className={`side-link ${range === d ? 'active' : ''}`} onClick={() => { setRange(d); closeModal(); toast(`Showing last ${d} days`, 'info') }}>
              <Icon name="calendar" size={18} /> Last {d} days
            </button>
          ))}
        </div>
      </>,
    )

  // ── Bento card style helper ─────────────────────────────────
  const bentoCard = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 18,
    boxShadow: '0 1px 0 rgba(255,255,255,.02), 0 4px 16px rgba(0,0,0,.12)',
  }

  return (
    <section>
      <AnnouncementBanner placement="dashboard" />
      {/* ── Header (unchanged) ── */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting()}, {firstName} 👋</h1>
          <p className="page-sub">Company-wide overview for {fmt.date(new Date())}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { setCustomFrom(""); setCustomTo(""); openDateMenu(); }}>
            <Icon name="calendar" size={16} /> {customFrom && customTo ? "Custom" : `Last ${range} days`}
          </button>
          <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", flex: "1 1 120px", minWidth: 0, maxWidth: 150 }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
          <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", flex: "1 1 120px", minWidth: 0, maxWidth: 150 }} />
          {(customFrom || customTo) && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => { setCustomFrom(""); setCustomTo(""); }}>Clear</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={remindAll}>
            <Icon name="bell" size={16} /> Remind all pending
          </button>
          <button className="btn btn-ghost btn-sm" onClick={exportData}>
            <Icon name="download" size={16} /> Export
          </button>
        </div>
      </div>

      {/* ── Alert strip (only when pending > 0) ── */}
      {pending > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', marginBottom: 16,
            borderRadius: 12,
            background: 'linear-gradient(90deg,rgba(239,68,68,.18),rgba(239,68,68,.06))',
            border: '1px solid rgba(239,68,68,.4)', color: '#991b1b', fontSize: 14,
          }}
        >
          <Icon name="alert-triangle" size={16} />
          <span>
            <strong style={{ color: '#7f1d1d' }}>{pending}</strong>{' '}
            employee{pending === 1 ? "" : 's'} {pending === 1 ? "hasn't" : "haven't"} submitted today
          </span>
          <button
            className="btn btn-sm"
            onClick={remindAll}
            style={{
              marginLeft: 'auto',
              background: '#dc2626',
              color: '#fff',
              border: '1px solid #b91c1c',
              fontWeight: 600,
            }}
          >
            <Icon name="bell" size={14} /> Remind All
          </button>
        </div>
      )}

      <ProjectsAtRiskCard />

      {/* ── BENTO ROW 1: Hero + mini stats ── */}
      <div className="dash-grid dash-grid--hero">
        {/* Hero compliance card — subtle purple tint, theme-aware */}
        <div
          style={{
            ...bentoCard,
            background:
              'linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(99,102,241,0.06) 60%, rgba(59,130,246,0.04) 100%)',
            border: '1px solid rgba(139,92,246,0.25)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 220,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                fontWeight: 600,
                color: 'var(--text-muted)',
              }}
            >
              Company Compliance
            </div>
            <div
              style={{
                fontSize: 'clamp(40px, 11vw, 64px)',
                fontWeight: 800,
                lineHeight: 1.05,
                marginTop: 6,
                background: 'linear-gradient(135deg,#7c3aed,#4f46e5,#2563eb)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {compliance}
              <span style={{ fontSize: 'clamp(20px, 6vw, 32px)', fontWeight: 600 }}>%</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              {activeEmployees} active employees · {todaysReports} reports today
            </div>
          </div>
          <div style={{ height: 56, marginTop: 8 }}>
            <canvas ref={heroSparkRef} />
          </div>
        </div>

        {/* Mini stats column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={bentoCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: 'var(--text-muted)' }}>
                  Reports Today
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{fmt.num(todaysReports)}</div>
              </div>
              <div style={{ width: 60, height: 28 }}><canvas ref={statSparkRef} /></div>
            </div>
          </div>
          <div style={bentoCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: 'var(--text-muted)' }}>
                  Managers
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{fmt.num(managerCount)}</div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => goPage('managers')}
                style={{ alignSelf: 'flex-start' }}
              >
                Manage →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── BENTO ROW 2: Full-width trend ── */}
      <div style={{ ...bentoCard, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Reports activity</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Submitted vs expected, last {range} days</div>
          </div>
        </div>
        <div style={{ height: 240 }}><canvas ref={trendRef} /></div>
      </div>

      {/* ── BENTO ROW 3: Top performers + Submission donut ── */}
      <div className="dash-grid dash-grid--wide-left">
        <div style={bentoCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Top performers</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>By report quality this week</div>
            </div>
            <button
              type="button"
              onClick={() => goPage('employees')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-2)', fontSize: 13, fontWeight: 600 }}
            >
              View all →
            </button>
          </div>
          {top.length ? (
            <div className="list-scroll">
            {top.slice(0, 5).map((e, i) => (
            <div key={e.id} className="activity-row">
              <div style={{ width: 28, textAlign: 'center', fontSize: 18 }}>
                {medals[i] || <span style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 600 }}>#{i + 1}</span>}
              </div>
              <Avatar name={e.name} seed={e.id.length + e.name.length} />
              <div className="activity-text">
                <div className="activity-title" style={{ fontWeight: 600 }}>{e.name}</div>
                <div className="activity-meta">{e.department || '—'} · {e.reportsCount} reports</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ color: 'var(--success)', fontWeight: 700, fontSize: 14 }}>{e.compliance}%</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>compliance</div>
              </div>
            </div>
            ))}
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', padding: 16, fontSize: 13 }}>No performance data yet.</div>
          )}
        </div>

        <div style={bentoCard}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Submission status</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Today's breakdown</div>
          </div>
          <div style={{ height: 200 }}><canvas ref={donutRef} /></div>
        </div>
      </div>

      {/* ── BENTO ROW 4: Pending submissions (left) + Recent activity timeline (right) ── */}
      <div className="dash-grid dash-grid--wide-right dash-grid--last">
        <div style={bentoCard}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Pending submissions</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {pendingWindow.active === false
                ? `Report due at ${pendingWindow.eveningTime || '18:00'}`
                : 'Missed the evening report · tap to remind'}
            </div>
          </div>
          {pendingEmps.length ? (
            <div className="list-scroll">
            {pendingEmps.map((e) => (
            <div key={e.id} className="activity-row">
              <Avatar name={e.name} seed={e.id.length + 5} />
              <div className="activity-text">
                <div className="activity-title" style={{ fontWeight: 600 }}>{e.name}</div>
                <div className="activity-meta">{e.department || '—'}</div>
              </div>
              <button
                className="btn btn-ghost btn-sm activity-view"
                disabled={!!reminding[e.id]}
                onClick={() => sendReminder(e.id)}
              >
                <Icon name="bell" size={14} /> {reminding[e.id] ? 'Sending…' : 'Remind'}
              </button>
            </div>
            ))}
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="ico"><Icon name={pendingWindow.active === false ? 'clock' : 'check-circle'} size={28} /></div>
              {pendingWindow.active === false ? (
                <>
                  <h3 style={{ fontSize: 15 }}>Report not due yet</h3>
                  <p style={{ fontSize: 12 }}>The evening prompt goes out at {pendingWindow.eveningTime || '18:00'}. Anyone who misses it will appear here.</p>
                </>
              ) : (
                <>
                  <h3 style={{ fontSize: 15 }}>All caught up!</h3>
                  <p style={{ fontSize: 12 }}>Everyone who got the evening prompt has submitted.</p>
                </>
              )}
            </div>
          )}
        </div>

        <div style={bentoCard}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Recent activity</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Live feed of submissions</div>
          </div>
          {feed.length ? (
            <div className="list-scroll">
            {feed.map((r, i) => (
            <div
              key={r.id}
              style={{
                display: 'flex', gap: 12, padding: '10px 0', alignItems: 'center',
                borderLeft: '2px solid var(--border)', paddingLeft: 16, marginLeft: 6, position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute', left: -6, top: 18, width: 10, height: 10,
                  borderRadius: '50%',
                  background: r.source === 'whatsapp' ? '#25D366' : 'var(--primary)',
                  boxShadow: '0 0 0 3px var(--surface)',
                }}
              />
              <Avatar name={r.employee.name} size="sm" seed={r.employee.id.length} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {r.employee.name}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                    {' '}submitted via{' '}
                  </span>
                  <span style={{ color: r.source === 'whatsapp' ? '#25D366' : 'var(--primary-2)', fontWeight: 600 }}>
                    {r.source === 'whatsapp' ? 'WhatsApp' : 'Web'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {r.taskCount} task{r.taskCount === 1 ? '' : 's'} · {fmt.rel(new Date(r.submittedAt))}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => viewReport(r)}>View</button>
              {i === feed.length - 1 && null}
            </div>
            ))}
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', padding: 16, fontSize: 13 }}>No recent submissions.</div>
          )}
        </div>
      </div>
    </section>
  )
}
